/**
 * Discussion WebSocket Service - Real-time discussion thread updates
 *
 * Story FOS-4.1.3: Real-Time Discussion Updates (WebSocket)
 * Task 6: Create client-side discussion WebSocket service
 *
 * Handles WebSocket connection to oracle-bridge for governance discussions.
 * Implements reconnection with exponential backoff.
 *
 * @see AC-4.1.3.1 - Users can subscribe to discussion threads
 * @see AC-4.1.3.5 - Client auto-reconnects with exponential backoff
 */

import {
  $discussionConnectionStatus,
  $discussionReconnectAttempts,
  setDiscussionConnectionStatus,
  incrementDiscussionReconnectAttempts,
  addRealtimeComment,
  retractComment,
  removeParticipant,
  addParticipant,
  updateDiscussionStage,
  setTypingIndicator,
  type RealtimeComment,
  type DiscussionParticipant,
} from '../stores/discussionStore';
import { getOracleBridgeUrl, debug } from './apiHelpers';

// =============================================================================
// Types (must match oracle-bridge/src/governance/types.ts)
// =============================================================================

export type AuthorType = { Human: null } | { Agent: { agent_id: string } };
export type DiscussionStage = 'Brainstorm' | 'Refining' | 'Ready';

// Client -> Server Messages
interface SubscribeThreadMessage {
  type: 'subscribe-thread';
  threadId: string;
  userId: string;
  displayName: string;
}

interface UnsubscribeThreadMessage {
  type: 'unsubscribe-thread';
  threadId: string;
}

interface PostCommentMessage {
  type: 'post-comment';
  threadId: string;
  commentId: string;
  content: string;
  authorId: string;
  authorName: string;
  authorType: AuthorType;
  timestamp: number;
}

// bl-1648-2: retract-comment / stage-change are NO LONGER emitted by the client.
// Those actions go through their REST endpoints, which server-originate the live
// comment-retracted / stage-changed frames (bl-1648-1). The inbound handlers for
// those server frames remain below.
interface TypingIndicatorMessage {
  type: 'typing-indicator';
  threadId: string;
  isTyping: boolean;
}

interface PingMessage {
  type: 'ping';
}

type ClientMessage =
  | SubscribeThreadMessage
  | UnsubscribeThreadMessage
  | PostCommentMessage
  | TypingIndicatorMessage
  | PingMessage;

// Server -> Client Messages
interface ThreadStateMessage {
  type: 'thread-state';
  threadId: string;
  participants: DiscussionParticipant[];
}

interface CommentPostedMessage {
  type: 'comment-posted';
  threadId: string;
  commentId: string;
  content: string;
  authorId: string;
  authorName: string;
  authorType: AuthorType;
  timestamp: number;
}

interface CommentRetractedMessage {
  type: 'comment-retracted';
  threadId: string;
  commentId: string;
  retractedAt: number;
}

interface ParticipantJoinMessage {
  type: 'participant-join';
  threadId: string;
  participant: DiscussionParticipant;
}

interface ParticipantLeaveMessage {
  type: 'participant-leave';
  threadId: string;
  participantId: string;
}

interface StageChangedMessage {
  type: 'stage-changed';
  threadId: string;
  newStage: DiscussionStage;
  changedAt: number;
}

interface TypingNotificationMessage {
  type: 'typing-notification';
  threadId: string;
  userId: string;
  displayName: string;
  isTyping: boolean;
}

interface PongMessage {
  type: 'pong';
}

interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
}

type ServerMessage =
  | ThreadStateMessage
  | CommentPostedMessage
  | CommentRetractedMessage
  | ParticipantJoinMessage
  | ParticipantLeaveMessage
  | StageChangedMessage
  | TypingNotificationMessage
  | PongMessage
  | ErrorMessage;

// =============================================================================
// Configuration
// =============================================================================

/** Base delay for reconnection (1 second) */
const BASE_RECONNECT_DELAY = 1000;

/** Maximum delay between reconnection attempts (30 seconds) */
const MAX_RECONNECT_DELAY = 30000;

/** Maximum number of reconnection attempts before giving up */
const MAX_RECONNECT_ATTEMPTS = 10;

/** Ping interval for keepalive (25 seconds) */
const PING_INTERVAL = 25000;

/**
 * Get the governance WebSocket URL from environment
 */
function getWebSocketUrl(): string {
  const baseUrl = getOracleBridgeUrl();
  // Convert http(s) to ws(s)
  const wsUrl = baseUrl.replace(/^http/, 'ws');
  return `${wsUrl}/ws/governance`;
}

// =============================================================================
// WebSocket State
// =============================================================================

let ws: WebSocket | null = null;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
let pingInterval: ReturnType<typeof setInterval> | null = null;
let currentThreadId: string | null = null;
let currentUserId: string | null = null;
let currentDisplayName: string | null = null;

// =============================================================================
// Connection Management
// =============================================================================

/**
 * Connect to the governance WebSocket server
 *
 * @see AC-4.1.3.5 - Client auto-reconnects with exponential backoff
 */
export function connect(): void {
  // Don't reconnect if already connected or connecting
  if (ws && (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN)) {
    return;
  }

  const url = getWebSocketUrl();
  debug.log('[discussionWS] Connecting to:', url);
  setDiscussionConnectionStatus('connecting');

  try {
    ws = new WebSocket(url);

    ws.onopen = () => {
      debug.log('[discussionWS] Connected');
      setDiscussionConnectionStatus('connected');

      // Reset reconnect attempts on successful connection
      // Re-subscribe to thread if we were subscribed before disconnect
      if (currentThreadId && currentUserId && currentDisplayName) {
        subscribeThread(currentThreadId, currentUserId, currentDisplayName);
      }

      // Start keepalive pings
      startPingInterval();
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data) as ServerMessage;
        handleMessage(message);
      } catch (error) {
        debug.error('[discussionWS] Failed to parse message:', error);
      }
    };

    ws.onclose = (event) => {
      debug.log(`[discussionWS] Disconnected: ${event.code} ${event.reason}`);
      cleanup();
      setDiscussionConnectionStatus('disconnected');
      scheduleReconnect();
    };

    ws.onerror = (error) => {
      debug.error('[discussionWS] Error:', error);
      setDiscussionConnectionStatus('error');
    };
  } catch (error) {
    debug.error('[discussionWS] Failed to create WebSocket:', error);
    setDiscussionConnectionStatus('error');
    scheduleReconnect();
  }
}

/**
 * Disconnect from the WebSocket server
 */
export function disconnect(): void {
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }

  cleanup();

  if (ws) {
    ws.close(1000, 'Client disconnect');
    ws = null;
  }

  setDiscussionConnectionStatus('disconnected');
}

/**
 * Clean up intervals and state
 */
function cleanup(): void {
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = null;
  }
}

/**
 * Schedule a reconnection attempt with exponential backoff
 *
 * @see AC-4.1.3.5 - Exponential backoff (1s, 2s, 4s, 8s, max 30s)
 */
function scheduleReconnect(): void {
  const attempts = $discussionReconnectAttempts.get();

  if (attempts >= MAX_RECONNECT_ATTEMPTS) {
    debug.log('[discussionWS] Max reconnect attempts reached, giving up');
    setDiscussionConnectionStatus('error');
    return;
  }

  // Calculate delay with exponential backoff
  const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, attempts), MAX_RECONNECT_DELAY);

  debug.log(`[discussionWS] Reconnecting in ${delay}ms (attempt ${attempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);

  incrementDiscussionReconnectAttempts();

  reconnectTimeout = setTimeout(() => {
    connect();
  }, delay);
}

/**
 * Start the keepalive ping interval
 */
function startPingInterval(): void {
  if (pingInterval) {
    clearInterval(pingInterval);
  }

  pingInterval = setInterval(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      send({ type: 'ping' });
    }
  }, PING_INTERVAL);
}

// =============================================================================
// Message Sending
// =============================================================================

/**
 * Send a message to the server
 */
function send(message: ClientMessage): boolean {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    debug.warn('[discussionWS] Cannot send - not connected');
    return false;
  }

  ws.send(JSON.stringify(message));
  return true;
}

// =============================================================================
// Thread Subscription
// =============================================================================

/**
 * Subscribe to a discussion thread
 *
 * @see AC-4.1.3.1 - Users can subscribe to discussion threads
 *
 * @param threadId - Discussion thread ID
 * @param userId - User's principal ID
 * @param displayName - User's display name
 */
export function subscribeThread(threadId: string, userId: string, displayName: string): void {
  // Store for reconnection
  currentThreadId = threadId;
  currentUserId = userId;
  currentDisplayName = displayName;

  // Connect if not connected
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    connect();
    return; // Will subscribe after connection
  }

  send({
    type: 'subscribe-thread',
    threadId,
    userId,
    displayName,
  });
}

/**
 * Unsubscribe from a discussion thread
 *
 * @param threadId - Discussion thread ID
 */
export function unsubscribeThread(threadId: string): void {
  // Clear stored subscription
  if (currentThreadId === threadId) {
    currentThreadId = null;
    currentUserId = null;
    currentDisplayName = null;
  }

  send({
    type: 'unsubscribe-thread',
    threadId,
  });
}

// =============================================================================
// Comment Broadcasting
// =============================================================================

/**
 * Broadcast a new comment to thread subscribers
 *
 * @see AC-4.1.3.2 - Comments broadcast within 300ms
 *
 * @param threadId - Discussion thread ID
 * @param comment - Comment data
 */
export function broadcastComment(
  threadId: string,
  comment: {
    commentId: string;
    content: string;
    authorId: string;
    authorName: string;
    authorType: AuthorType;
  }
): void {
  send({
    type: 'post-comment',
    threadId,
    commentId: comment.commentId,
    content: comment.content,
    authorId: comment.authorId,
    authorName: comment.authorName,
    authorType: comment.authorType,
    timestamp: Date.now(),
  });
}

// bl-1648-2: broadcastRetraction / broadcastStageChange REMOVED. Comment retraction
// and stage transitions are performed via their REST endpoints, which server-
// originate the live comment-retracted / stage-changed frames (bl-1648-1). The
// client no longer emits these frames; the inbound handlers (handleCommentRetracted
// / handleStageChanged, below) still consume the server broadcast.

// =============================================================================
// Typing Indicator
// =============================================================================

/**
 * Send typing indicator
 *
 * @param threadId - Discussion thread ID
 * @param isTyping - Whether the user is currently typing
 */
export function sendTypingIndicator(threadId: string, isTyping: boolean): void {
  send({
    type: 'typing-indicator',
    threadId,
    isTyping,
  });
}

// =============================================================================
// Message Handling
// =============================================================================

/**
 * Handle incoming server message
 */
function handleMessage(message: ServerMessage): void {
  switch (message.type) {
    case 'thread-state':
      handleThreadState(message);
      break;
    case 'comment-posted':
      handleCommentPosted(message);
      break;
    case 'comment-retracted':
      handleCommentRetracted(message);
      break;
    case 'participant-join':
      handleParticipantJoin(message);
      break;
    case 'participant-leave':
      handleParticipantLeave(message);
      break;
    case 'stage-changed':
      handleStageChanged(message);
      break;
    case 'typing-notification':
      handleTypingNotification(message);
      break;
    case 'pong':
      // Keepalive response - no action needed
      break;
    case 'error':
      handleError(message);
      break;
    default:
      debug.warn('[discussionWS] Unknown message type:', (message as ServerMessage).type);
  }
}

function handleThreadState(message: ThreadStateMessage): void {
  debug.log(`[discussionWS] Thread state received: ${message.participants.length} participants`);
  // Participants will be stored in discussionStore
  for (const participant of message.participants) {
    addParticipant(participant);
  }
}

function handleCommentPosted(message: CommentPostedMessage): void {
  debug.log(`[discussionWS] Comment posted: ${message.commentId}`);
  const comment: RealtimeComment = {
    id: message.commentId,
    threadId: message.threadId,
    content: message.content,
    authorId: message.authorId,
    authorName: message.authorName,
    authorType: message.authorType,
    timestamp: message.timestamp,
    isRetracted: false,
  };
  addRealtimeComment(comment);
}

function handleCommentRetracted(message: CommentRetractedMessage): void {
  debug.log(`[discussionWS] Comment retracted: ${message.commentId}`);
  retractComment(message.commentId, message.retractedAt);
}

function handleParticipantJoin(message: ParticipantJoinMessage): void {
  debug.log(`[discussionWS] Participant joined: ${message.participant.displayName}`);
  addParticipant(message.participant);
}

function handleParticipantLeave(message: ParticipantLeaveMessage): void {
  debug.log(`[discussionWS] Participant left: ${message.participantId}`);
  removeParticipant(message.participantId);
}

function handleStageChanged(message: StageChangedMessage): void {
  debug.log(`[discussionWS] Stage changed to: ${message.newStage}`);
  updateDiscussionStage(message.newStage, message.changedAt);
}

function handleTypingNotification(message: TypingNotificationMessage): void {
  debug.log(`[discussionWS] ${message.displayName} ${message.isTyping ? 'is typing' : 'stopped typing'}`);
  setTypingIndicator(message.userId, message.displayName, message.isTyping);
}

function handleError(message: ErrorMessage): void {
  debug.error(`[discussionWS] Server error: ${message.code} - ${message.message}`);
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Check if connected to WebSocket server
 */
export function isConnected(): boolean {
  return ws !== null && ws.readyState === WebSocket.OPEN;
}

/**
 * Get current connection status
 */
export function getConnectionStatus(): 'connected' | 'connecting' | 'disconnected' | 'error' {
  return $discussionConnectionStatus.get();
}

/**
 * Force reconnection (resets attempt counter)
 */
export function forceReconnect(): void {
  // Clear any pending reconnect
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }

  // Close existing connection
  if (ws) {
    ws.close(1000, 'Force reconnect');
    ws = null;
  }

  // Reset attempt counter and state, then reconnect
  $discussionReconnectAttempts.set(0);
  setDiscussionConnectionStatus('connecting');
  connect();
}
